import { Component, OnDestroy, OnInit } from '@angular/core';
import { NbDialogService, NbTooltipModule } from '@nebular/theme';
import { combineLatest, of, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { KPI } from '../../../@core/api';
import { PerimetersService } from '../../../@core/utils';
import { PerimetersFiltersDialogComponent } from '../perimeters-filters-dialog/perimeters-filters-dialog.component';
import { faCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgClass } from '@angular/common';
import { IS_FIRST_CONNECTION_STORAGE_KEY } from '../../../@core/utils/constants';
import { Router } from '@angular/router';


@Component({
  selector: 'app-perimeters-filters-button',
  styleUrls: ['./perimeters-filters-button.component.scss'],
  templateUrl: './perimeters-filters-button.component.html',
  standalone: true,
  imports: [
    NgClass,
    FontAwesomeModule,
    NbTooltipModule,
    TranslateModule
  ],
})
export class PerimetersFiltersButtonComponent implements OnInit, OnDestroy {
  public perimetersParams = { label: '' };
  public focusedPerimetersParams = { label: '' };
  public focusedPerimeter: boolean = false;
  public clearIcon = faCircleXmark;
  public clearIconTooltip = this.translate.instant('Back to previous perimeters');

  public selectedPerimetersMap: { [id: number]: boolean } = {};
  private subscriptions: Subscription[] = [];
  private perimeterDialogOpened: boolean = false;

  constructor(
    private dialogService: NbDialogService,
    private perimetersService: PerimetersService,
    private translate: TranslateService,
    private router: Router
  ) { }

  public ngOnInit(): void {
    this.subscriptions = [
      // Display correct label on perimeter change
      combineLatest([
        this.perimetersService.perimeters$,
        this.perimetersService.previousPerimeters$
      ]).pipe(
        switchMap(([perimeters, previousPerimeters]) => {
          /* if (!perimeters?.length && !previousPerimeters?.length) {
            this.openFiltersDialog(true);
          } */

          if (previousPerimeters?.length) {
            return combineLatest([
              this.perimetersService.getPerimetersLabel(perimeters),
              this.perimetersService.getPerimetersLabel(previousPerimeters),
            ]);
          } else {
            return combineLatest([
              this.perimetersService.getPerimetersLabel(perimeters),
              of(null),
            ]);
          }
        }),
      ).subscribe(([perimetersLabel, previousPerimetersLabel]) => {
        if (previousPerimetersLabel) {
          this.focusedPerimeter = true;
          this.perimetersParams = { label: previousPerimetersLabel }
          this.focusedPerimetersParams = { label: perimetersLabel };
        } else {
          this.focusedPerimeter = false;
          this.perimetersParams = { label: perimetersLabel }
          this.focusedPerimetersParams = { label: '' };
        }
      }),
    ];
    if (!this.perimetersService.perimetersLoaded) {
      this.subscriptions.push(this.perimetersService.loadPerimeters());
    }

    // If user just logged in, select all the sites and go to the map
    const isFirstConnection = localStorage.getItem(
      IS_FIRST_CONNECTION_STORAGE_KEY,
    );
    if ((isFirstConnection === null || isFirstConnection === 'true')) {
      this.router.navigate(['sites', 'map'], {
        replaceUrl: true
      });
      this.perimetersService.perimetersHaveChanged();
      this.perimetersService.filteredPerimeters$.subscribe(filteredPerimeters => {
        this.perimetersService.updateSelectedPerimeters(
          filteredPerimeters,
        );
        this.perimetersService.perimetersHaveChanged();
        localStorage.setItem(IS_FIRST_CONNECTION_STORAGE_KEY, 'false');
      })
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public openFiltersDialog(disableClosingDiablog: boolean): void {
    if (!this.perimeterDialogOpened) {
      this.perimeterDialogOpened = true;
      this.perimetersService.perimetersHaveChanged();

      const filtersDialog = this.dialogService.open(
        PerimetersFiltersDialogComponent,
        {
          closeOnBackdropClick: !disableClosingDiablog,
          context: {
            disableClosingDiablog: disableClosingDiablog,
          },
          autoFocus: false,
        },
      );

      filtersDialog.onClose.subscribe(() => {
        this.perimeterDialogOpened = false;
        this.perimetersService.perimetersHaveChanged();
      });
    }
  }

  private getPerimetersMap(
    perimeters: KPI.Perimeter[],
    selectedPerimeters: KPI.Perimeter[],
  ) {
    return perimeters.reduce((acc, perimeter) => {
      const selectedPerimeterIndex = selectedPerimeters.findIndex(
        (p) => p.id === perimeter.id,
      );

      acc[perimeter.id] =
        acc[perimeter.id] !== undefined
          ? acc[perimeter.id]
          : selectedPerimeterIndex > -1;

      if (perimeter.sub_perimeters) {
        if (selectedPerimeterIndex > -1) {
          const selectedPerimeterSubPerimeters =
            selectedPerimeters[selectedPerimeterIndex].sub_perimeters;
          acc = {
            ...acc,
            ...this.getPerimetersMap(
              perimeter.sub_perimeters,
              selectedPerimeterSubPerimeters,
            ),
          };
        } else {
          acc = {
            ...acc,
            ...this.getPerimetersMap(perimeter.sub_perimeters, []),
          };
        }
      }
      return acc;
    }, {});
  }

  public goBackToPreviousPerimeters(): void {
    this.perimetersService.goBackToPreviousPerimeters();
  }

}
